/* eslint-disable max-lines */
'use client';

import { FloatingDialer } from './FloatingDialer';
import { MobileDialer } from './MobileDialer';
import { DraggableClientInfoModal } from './DraggableClientInfoModal';
import { DraggableQualificationModal } from './DraggableQualificationModal';
import {
  useCallOperatorState,
  useCallOperatorCurrentCallInfo,
  useCallOperatorCurrentCall,
  useCallOperatorSuccessQualifications,
  useCallOperatorDiscardQualifications,
  useCallOperatorControls,
} from '../lib/callixSdk';
import type { CallInteractionResult } from '@callixbrasil/client-sdk';
import { useEffect, useState, useMemo } from 'react';
import { useDialerConfig } from '../widget/index';
import type { DialerDensity } from '../lib/deviceProfile';
import type { LoginConfirmation } from '../lib/dialerTabs';

type InlineDialerProps = {
  isAuthenticated: boolean;
  username: string | null;
  onLogin: (username: string) => Promise<void> | void;
  onLogout: () => void;
  isLoadingSession: boolean;
  sessionError: string | null;
  /** Mensagem de progresso do login (ex.: "Autenticando no Callix...") */
  sessionStatus?: string | null;
  /** Usuário conhecido (HTML ou último login) aguardando confirmação para entrar */
  loginConfirmation?: LoginConfirmation | null;
  /** Trocar de usuário na tela de confirmação */
  onCancelLoginConfirmation?: () => void;
  /** Outra aba segura a conexão: trazer o discador para esta aba */
  onTakeOverSession?: () => void;
  /** Sair do discador em todas as abas */
  onLogoutAllTabs?: () => void;
  /** Densidade da interface ('auto' | 'compact' | 'comfortable') */
  density?: DialerDensity;
  variant?: 'default' | 'min' | 'mobile';
  portalTarget?: HTMLElement | null;
  mode?: 'floating' | 'inline';  // Modo de exibição passado do widget
};

/**
 * InlineDialer - decide qual dialer renderizar de acordo com o variant.
 * - mobile: UI nova mobile.
 * - min: mini inline autenticado.
 * - default: FloatingDialer padrão.
 */
export function InlineDialer({ variant = 'default', portalTarget, isAuthenticated, mode, ...props }: InlineDialerProps) {
  const isMiniMode = variant === 'min';

  if (variant === 'mobile') {
    // Se mode='inline', passa showClientInfoModal=true para exibir o modal de info
    return <MobileDialer isAuthenticated={isAuthenticated} showClientInfoModal={mode === 'inline'} {...props} />;
  }

  // Se não autenticado OU não é mini mode, delegar para FloatingDialer
  // FloatingDialer tem sua própria tela de login
  if (!isAuthenticated || !isMiniMode) {
    return <FloatingDialer variant={variant} portalTarget={portalTarget} isAuthenticated={isAuthenticated} {...props} />;
  }

  // Se autenticado E mini mode, renderizar componente autenticado
  return <AuthenticatedMiniDialer portalTarget={portalTarget} {...props} />;
}

/**
 * Componente interno que SÓ é renderizado quando autenticado
 * Todos os hooks do SDK são seguros aqui porque CallixClientProvider está presente
 */
function AuthenticatedMiniDialer({
  username,
  onLogin,
  onLogout,
  isLoadingSession,
  sessionError,
  portalTarget,
}: Omit<InlineDialerProps, 'isAuthenticated' | 'variant'>) {
  // Configuração do dialer (skipQualification para chamadas manuais)
  const { skipManualQualification } = useDialerConfig();

  // Hooks do SDK
  const operatorState = useCallOperatorState();
  const currentCallInfo = useCallOperatorCurrentCallInfo();
  const currentCall = useCallOperatorCurrentCall();
  const rawSuccessQualifications = useCallOperatorSuccessQualifications();
  const rawDiscardQualifications = useCallOperatorDiscardQualifications();
  const successQualifications = useMemo(() => rawSuccessQualifications ?? [], [rawSuccessQualifications]);
  const discardQualifications = useMemo(() => rawDiscardQualifications ?? [], [rawDiscardQualifications]);
  const { finishAfterCall } = useCallOperatorControls();

  // Estados para modais (sempre criados)
  const [isClientInfoModalOpen, setIsClientInfoModalOpen] = useState(false);
  const [isQualificationModalOpen, setIsQualificationModalOpen] = useState(false);
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);
  const [afterCallResult, setAfterCallResult] = useState<CallInteractionResult>('success');
  const [selectedQualificationId, setSelectedQualificationId] = useState<number | null>(null);
  const [isSubmittingAfterCall, setIsSubmittingAfterCall] = useState(false);
  const [afterCallError, setAfterCallError] = useState<string | null>(null);

  const callState = operatorState?.state;
  const isInCall = callState === 'callInProgress';
  const isRinging = callState === 'manualCallRinging' || callState === 'callRinging';
  const isConnecting = callState === 'manualCallSetup' || isRinging;
  const isAfterCall = callState === 'afterCall';

  // Contador de duração da chamada
  useEffect(() => {
    if (!isInCall) {
      setCallDurationSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      setCallDurationSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isInCall]);

  // Abrir modal de informações do cliente para chamadas de campanha (entrantes ou SAÍDAS)
  useEffect(() => {
    if ((isInCall || isConnecting) && currentCallInfo?.type === 'campaign') {
      setIsClientInfoModalOpen(true);
    }
  }, [isInCall, isConnecting, currentCallInfo]);

  // Abrir modal de qualificação quando entrar em afterCall
  useEffect(() => {
    if (isAfterCall) {
      // Verificar se deve pular qualificação (campanhas)
      const shouldSkipCampaignQualification =
        currentCallInfo?.type === 'campaign' &&
        currentCallInfo.info.campaign.skipCallQualification === true;

      if (shouldSkipCampaignQualification && finishAfterCall) {
        // Auto-finalizar sem qualificação
        void Promise.resolve(
          finishAfterCall({
            result: 'success',
            qualificationId: undefined,
          })
        );
        return;
      }

      // Chamadas manuais SEMPRE pulam qualificação (não faz sentido qualificar chamada manual)
      const isManualCall = currentCallInfo?.type === 'manual';

      if (isManualCall && finishAfterCall) {
        console.log('[InlineDialer] 🚀 Chamada MANUAL detectada - finalizando automaticamente (chamadas manuais NUNCA exibem qualificação)');
        // Auto-finalizar sem qualificação
        void Promise.resolve(
          finishAfterCall({
            result: 'success',
            qualificationId: undefined,
          })
        );
        return;
      }

      // Check if no qualifications configured
      const hasNoQualifications = successQualifications.length === 0 && discardQualifications.length === 0;
      if (hasNoQualifications && finishAfterCall) {
        console.log('[InlineDialer] 🚀 Sem qualificações configuradas - finalizando automaticamente');
        void Promise.resolve(
          finishAfterCall({
            result: 'success',
            qualificationId: undefined,
          })
        );
        return;
      }

      // Abrir modal de qualificação
      const hasSuccessQualification = successQualifications.length > 0;
      setAfterCallResult(hasSuccessQualification ? 'success' : 'failure');
      setSelectedQualificationId(null);
      setAfterCallError(null);
      setIsQualificationModalOpen(true);
    } else {
      setIsQualificationModalOpen(false);
    }
  }, [isAfterCall, currentCallInfo, finishAfterCall, successQualifications, discardQualifications, skipManualQualification]);

  // Fechar modal de cliente quando chamada terminar (mas não durante afterCall)
  useEffect(() => {
    if (!isInCall && !isConnecting && !isAfterCall) {
      setIsClientInfoModalOpen(false);
    }
  }, [isInCall, isConnecting, isAfterCall]);

  // Obter campaignCallInfo completo (igual ao CallInfoDisplay)
  const campaignCallInfo = useMemo(() => {
    if (!currentCallInfo || currentCallInfo.type !== 'campaign') {
      return null;
    }

    return currentCallInfo.info;
  }, [currentCallInfo]);

  // Handler para finalizar qualificação
  const handleFinishAfterCall = async () => {
    const qualifications = afterCallResult === 'success' ? successQualifications : discardQualifications;
    if (!qualifications.length) {
      setAfterCallError('Nenhuma qualificação configurada para este resultado.');
      return;
    }

    if (!selectedQualificationId) {
      setAfterCallError('Selecione uma qualificação para finalizar.');
      return;
    }

    if (typeof finishAfterCall !== 'function') {
      setAfterCallError('Não foi possível enviar a qualificação.');
      return;
    }

    setIsSubmittingAfterCall(true);
    setAfterCallError(null);

    try {
      await Promise.resolve(
        finishAfterCall({
          result: afterCallResult,
          qualificationId: selectedQualificationId,
        })
      );
      setIsQualificationModalOpen(false);
    } catch (error) {
      setAfterCallError('Erro ao enviar qualificacao.');
      console.error('Erro ao finalizar afterCall:', error);
    } finally {
      setIsSubmittingAfterCall(false);
    }
  };

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            /* Container externo do HTML - SEM padding/border para não vazar */
            #dialer-inline.callix.mode.min {
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
              padding: 0 !important;
              border: none !important;
              background: transparent !important;
              box-shadow: none !important;
            }

            /* Estilos para modo mini quando Tailwind CDN não processa classes */
            .callix.mode.min {
              display: flex !important;
              flex-direction: column !important;
              gap: 0.75rem !important;
              border-radius: 1rem !important;
              border: 1px solid #e2e8f0 !important;
              background: rgba(255, 255, 255, 0.95) !important;
              padding: 0.75rem !important;
              box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
              color: #1e293b !important;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }

            /* Visor de número - fonte menor para caber todos os dígitos */
            .callix.mode.min [class*="truncate"] {
              font-size: 0.875rem !important; /* text-sm */
              line-height: 1.25rem !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
              white-space: nowrap !important;
            }

            /* Botões do modo mini */
            .callix.mode.min button {
              cursor: pointer !important;
              transition: all 0.2s ease !important;
            }
            
            .callix.mode.min button:disabled {
              cursor: not-allowed !important;
              opacity: 0.6 !important;
            }

            /* Grid do teclado numérico */
            .callix.mode.min [class*="grid-cols-3"] {
              display: grid !important;
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
              gap: 0.5rem !important;
            }

            /* Botões do teclado */
            .callix.mode.min [class*="grid-cols-3"] button {
              display: flex !important;
              height: 3rem !important;
              flex-direction: column !important;
              align-items: center !important;
              justify-content: center !important;
              border-radius: 0.5rem !important;
              border: 1px solid #e2e8f0 !important;
              background: white !important;
              font-size: 1rem !important;
              font-weight: 600 !important;
              color: #1e293b !important;
              box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
            }

            .callix.mode.min [class*="grid-cols-3"] button:hover:not(:disabled) {
              border-color: #cbd5e1 !important;
              box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
            }

            .callix.mode.min [class*="grid-cols-3"] button:active:not(:disabled) {
              transform: scale(0.95) !important;
            }

            /* Botão principal de Ligar/Encerrar */
            .callix.mode.min > button[class*="bg-emerald"],
            .callix.mode.min > button[class*="bg-rose"] {
              display: flex !important;
              height: 2.75rem !important;
              width: 100% !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 0.5rem !important;
              border-radius: 0.75rem !important;
              font-size: 0.875rem !important;
              font-weight: 700 !important;
              color: white !important;
              box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
              border: none !important;
            }

            .callix.mode.min > button[class*="bg-emerald"] {
              background: #10b981 !important;
            }

            .callix.mode.min > button[class*="bg-emerald"]:hover:not(:disabled) {
              background: #059669 !important;
            }

            .callix.mode.min > button[class*="bg-rose"] {
              background: #f43f5e !important;
            }

            .callix.mode.min > button[class*="bg-rose"]:hover:not(:disabled) {
              background: #e11d48 !important;
            }
          `,
        }}
      />

      {/* FloatingDialer em modo mini */}
      <FloatingDialer
        isAuthenticated={true}
        username={username}
        onLogin={onLogin}
        onLogout={onLogout}
        isLoadingSession={isLoadingSession}
        sessionError={sessionError}
        variant="min"
      />

      {/* Modais para modo mini */}
      <DraggableClientInfoModal
        isOpen={isClientInfoModalOpen}
        campaignCallInfo={campaignCallInfo}
        campaignContactPhoneId={currentCall?.data?.campaignContactPhoneId ?? null}
        callDuration={callDurationSeconds}
        isCallActive={isInCall || isConnecting}
        onClose={() => setIsClientInfoModalOpen(false)}
        portalTarget={portalTarget}
      />

      <DraggableQualificationModal
        isOpen={isQualificationModalOpen}
        result={afterCallResult}
        onChangeResult={setAfterCallResult}
        successQualifications={successQualifications}
        discardQualifications={discardQualifications}
        selectedQualificationId={selectedQualificationId}
        onSelectQualification={setSelectedQualificationId}
        onSubmit={handleFinishAfterCall}
        isSubmitting={isSubmittingAfterCall}
        errorMessage={afterCallError}
      />
    </>
  );
}
